import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../utils/authFetch';
import { Badge, Card, Checkbox } from '../components/ui';

/*
  Aggiornamento firmware del parco macchine.

  Mostra tutti i device installati con la versione firmware riportata dal
  device stesso, lo stato rispetto alla versione di riferimento (aggiornato,
  non aggiornato, obsoleto, in attesa, fallito) e permette di mettere in coda
  l'aggiornamento OTA verso un firmware dell'archivio. I device scaricano il
  .bin al prossimo polling dei comandi (10 s) e confermano con un ACK dopo il
  riavvio.
*/

const NOT_UP_TO_DATE = ['outdated', 'obsolete', 'unknown', 'failed'];

const emptyFilters = {
  q: '',
  customer: '',
  site: '',
  version: '',
  status: '',
  installedFrom: '',
  installedTo: '',
  updatedFrom: '',
  updatedTo: '',
  onlyOnline: false,
  onlyIssues: false,
};

const STATUS_META = {
  up_to_date: {
    label: 'Aggiornato',
    className: 'bg-emerald-100 text-emerald-700',
    hint: 'Versione uguale o successiva a quella di riferimento',
  },
  outdated: {
    label: 'Non aggiornato',
    className: 'bg-amber-100 text-amber-700',
    hint: 'Versione precedente a quella di riferimento',
  },
  obsolete: {
    label: 'Obsoleto',
    className: 'bg-red-100 text-red-700',
    hint: 'Versione deprecata o non presente in archivio',
  },
  unknown: {
    label: 'Sconosciuto',
    className: 'bg-gray-100 text-gray-600',
    hint: 'Il device non ha mai riportato una versione',
  },
  no_reference: {
    label: 'Nessun riferimento',
    className: 'bg-gray-100 text-gray-600',
    hint: "Imposta un firmware come riferimento nell'archivio",
  },
  pending: {
    label: 'In attesa',
    className: 'bg-cyan-100 text-cyan-700',
    hint: 'Aggiornamento in coda: il device lo scarica al prossimo contatto',
  },
  failed: {
    label: 'Fallito',
    className: 'bg-red-100 text-red-700',
    hint: "L'ultimo aggiornamento non è riuscito (vedi dettagli)",
  },
};

const FW_STATUS_LABEL = {
  testing: 'In test',
  stable: 'Stabile',
  deprecated: 'Deprecato',
};

// Fasi riportate dal device durante l'aggiornamento (POST /api/iot/firmware/status)
const OTA_PHASE_LABEL = {
  queued: 'in coda',
  requested: 'preso in carico',
  downloading: 'scaricamento',
  verifying: 'verifica',
  verified: 'verificato',
  installing: 'installazione',
  rebooting: 'riavvio',
  success: 'completato',
  rolled_back: 'ripristinata la versione precedente',
  failed_hw: 'hardware incompatibile',
  failed_size: 'dimensione errata',
  failed_download: 'download fallito',
  failed_hash: 'SHA256 errato',
  failed_flash: 'scrittura fallita',
};

const MODBUS_CFG_LABEL = {
  pending: 'in attesa del device',
  done: 'applicata',
  failed: 'rifiutata',
};

// Revisione della scheda madre attuale: finisce nel manifest OTA e il
// firmware rifiuta un binario compilato per un'altra scheda.
const DEFAULT_HW_REVISION = 'MB-31-07-26';

// Limiti del firmware (modbusApplyConfigJson): al massimo 4 slave
const MODBUS_MAX_SLAVES = 4;

// Uno slave come lo accetta il firmware: {id, fc, reg, n, model, label, scale, enabled}
function defaultModbusSlave(pos) {
  return {
    id: pos,
    fc: 4,
    reg: 1,
    n: 2,
    model: 'XY-MD04',
    label: `MB${pos}`,
    scale: 1,
    enabled: true,
  };
}

/*
  Riporta la lista del device in un formato modificabile. Prima la lista
  inviata dalla dashboard, poi quella riportata dalla diagnostica (che non
  dice funzione e registro: per quelli si parte dai valori del modello).
*/
function slavesFromDevice(device) {
  const requested = device?.modbus_config?.slaves;
  if (Array.isArray(requested) && requested.length > 0) {
    return requested.map((s, i) => ({ ...defaultModbusSlave(i + 1), ...s }));
  }

  const reported = device?.last_diag?.modbus_slaves;
  if (Array.isArray(reported) && reported.length > 0) {
    return reported.map((s, i) => ({
      ...defaultModbusSlave(i + 1),
      id: s.id ?? i + 1,
      label: s.label || `MB${i + 1}`,
      model: s.model === 'generic' ? 'generic' : 'XY-MD04',
      enabled: s.enabled !== false,
    }));
  }

  return [defaultModbusSlave(1), defaultModbusSlave(2)];
}

const inputClass =
  'mt-1 block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-800 focus:border-blue-500 focus:outline-none';

const smallButtonClass =
  'rounded-lg border px-2 py-1 text-xs font-medium transition disabled:cursor-not-allowed disabled:opacity-50';

function formatDate(value, withTime = true) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  });
}

function toDateInput(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function relativeTime(value) {
  if (!value) return 'mai';
  const diff = Date.now() - new Date(value).getTime();
  if (Number.isNaN(diff)) return '—';
  const minutes = Math.round(diff / 60000);
  if (minutes < 1) return 'adesso';
  if (minutes < 60) return `${minutes} min fa`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours} h fa`;
  return `${Math.round(hours / 24)} giorni fa`;
}

function inDateRange(value, from, to) {
  if (!from && !to) return true;
  if (!value) return false;
  const time = new Date(value).getTime();
  if (Number.isNaN(time)) return false;
  if (from && time < new Date(from).getTime()) return false;
  if (to && time > new Date(`${to}T23:59:59`).getTime()) return false;
  return true;
}

function uptimeLabel(seconds) {
  if (seconds === null || seconds === undefined) return '—';
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (days > 0) return `${days} g ${hours} h`;
  if (hours > 0) return `${hours} h ${minutes} min`;
  return `${minutes} min`;
}

function formatBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} kB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

async function readError(response, fallback) {
  if (!response) return fallback;
  const data = await response.json().catch(() => null);
  return data?.message || data?.error || fallback;
}

export default function AggiornamentoFirmware() {
  const navigate = useNavigate();
  const role = String(localStorage.getItem('role') || '').toLowerCase();
  const canManage = ['admin', 'gestione', 'tecnico'].includes(role);
  // Il binario contiene il token di ingest: lo scaricano solo admin e gestione
  const canDownloadBinary = ['admin', 'gestione'].includes(role);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [filters, setFilters] = useState(emptyFilters);
  const [selected, setSelected] = useState(() => new Set());
  const [targetFirmwareId, setTargetFirmwareId] = useState('');
  const [expanded, setExpanded] = useState(null);

  const [busy, setBusy] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const [actionError, setActionError] = useState('');

  const [showMigration, setShowMigration] = useState(false);
  const [migrationUrl, setMigrationUrl] = useState('');

  const [showModbus, setShowModbus] = useState(false);
  const [modbusSlaves, setModbusSlaves] = useState(() => slavesFromDevice(undefined));

  const [showArchive, setShowArchive] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadVersion, setUploadVersion] = useState('');
  const [uploadVariant, setUploadVariant] = useState('prod');
  const [uploadHwRevision, setUploadHwRevision] = useState(DEFAULT_HW_REVISION);
  const [uploadStatus, setUploadStatus] = useState('stable');
  const [uploadNotes, setUploadNotes] = useState('');
  const [uploadLatest, setUploadLatest] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadInputKey, setUploadInputKey] = useState(0);

  const loadFleet = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      setError('');

      const res = await authFetch('/api/iot/fleet');

      if (!res || !res.ok) {
        throw new Error(await readError(res, 'Errore nel recupero del parco macchine'));
      }

      const payload = await res.json();
      setData(payload);
      setTargetFirmwareId((current) => current || payload.latest_firmware_id || '');
    } catch (err) {
      console.error('Errore fetch fleet:', err);
      setError(err?.message || 'Impossibile recuperare il parco macchine.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFleet();
    const timer = window.setInterval(() => loadFleet(true), 30000);
    return () => window.clearInterval(timer);
  }, [loadFleet]);

  const devices = useMemo(() => data?.devices ?? [], [data]);
  const firmwares = useMemo(() => data?.firmwares ?? [], [data]);

  const filteredDevices = useMemo(() => {
    const q = filters.q.trim().toLowerCase();

    return devices.filter((device) => {
      if (filters.customer && (device.customer_name || '') !== filters.customer) return false;
      if (filters.site && (device.installation_site || '') !== filters.site) return false;
      if (filters.version && (device.fw_version || '') !== filters.version) return false;
      if (filters.status) {
        if (filters.status === 'not_up_to_date') {
          if (!NOT_UP_TO_DATE.includes(device.fw_status)) return false;
        } else if (device.fw_status !== filters.status) {
          return false;
        }
      }
      if (filters.onlyOnline && !device.online) return false;
      if (filters.onlyIssues && !['warning', 'error'].includes(device.last_diag?.level || ''))
        return false;
      if (!inDateRange(device.installed_at, filters.installedFrom, filters.installedTo))
        return false;
      if (!inDateRange(device.fw_updated_at, filters.updatedFrom, filters.updatedTo)) return false;

      if (q) {
        const haystack = [
          device.device_id,
          device.machine_id,
          device.machine_name,
          device.machine_serial,
          device.fridge_serial,
          device.customer_name,
          device.installation_site,
          device.room,
          device.fw_version,
          device.ip_address,
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();

        if (!haystack.includes(q)) return false;
      }

      return true;
    });
  }, [devices, filters]);

  const versionDistribution = useMemo(() => {
    const counts = new Map();
    devices.forEach((device) => {
      const key = device.fw_version || '(nessuna)';
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [devices]);

  const allFilteredSelected =
    filteredDevices.length > 0 && filteredDevices.every((d) => selected.has(d.device_id));

  const toggleSelected = (deviceId) => {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(deviceId)) next.delete(deviceId);
      else next.add(deviceId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelected((current) => {
      const next = new Set(current);
      if (allFilteredSelected) {
        filteredDevices.forEach((d) => next.delete(d.device_id));
      } else {
        filteredDevices.forEach((d) => next.add(d.device_id));
      }
      return next;
    });
  };

  const selectNotUpToDate = () => {
    setSelected(
      new Set(
        filteredDevices
          .filter((d) => NOT_UP_TO_DATE.includes(d.fw_status))
          .map((d) => d.device_id)
      )
    );
  };

  const updateFilter = (key, value) => {
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const requestUpdate = async () => {
    if (!targetFirmwareId || selected.size === 0) return;

    const firmware = firmwares.find((fw) => fw._id === targetFirmwareId);
    const confirmed = window.confirm(
      `Mettere in coda l'aggiornamento a ${firmware?.version || '?'} (${firmware?.variant || ''}) per ${selected.size} device?`
    );
    if (!confirmed) return;

    try {
      setBusy(true);
      setActionMessage('');
      setActionError('');

      const res = await authFetch('/api/iot/fleet/update', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_ids: [...selected], firmware_id: targetFirmwareId }),
      });

      if (!res || !res.ok) {
        throw new Error(await readError(res, 'Richiesta aggiornamento non riuscita'));
      }

      const result = await res.json();
      const skipped = (result.results || []).filter((r) => !r.ok);

      setActionMessage(
        `Aggiornamento in coda per ${result.requested} device` +
          (skipped.length
            ? `, ${skipped.length} saltati (${skipped
                .map((r) => `${r.device_id}: ${r.reason}`)
                .join('; ')})`
            : '')
      );
      setSelected(new Set());
      await loadFleet(true);
    } catch (err) {
      setActionError(err?.message || 'Errore richiesta aggiornamento');
    } finally {
      setBusy(false);
    }
  };

  const updateModbusSlave = (index, patch) => {
    setModbusSlaves((list) => list.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  };

  const loadModbusFromSelected = () => {
    const first = [...selected][0];
    const device = devices.find((d) => d.device_id === first);
    setModbusSlaves(slavesFromDevice(device));
  };

  const requestModbusConfig = async () => {
    if (selected.size === 0 || modbusSlaves.length === 0) return;

    const summary = modbusSlaves
      .map((s) => `${s.label} = id ${s.id}, ${s.model}${s.enabled ? '' : ' (spento)'}`)
      .join('\n');
    const confirmed = window.confirm(
      `Inviare questa lista di slave a ${selected.size} device?\n\n${summary}\n\n` +
        'Il device la applica al prossimo contatto e la conserva anche dopo un riavvio.'
    );
    if (!confirmed) return;

    try {
      setBusy(true);
      setActionMessage('');
      setActionError('');

      const res = await authFetch('/api/iot/fleet/modbus-config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_ids: [...selected], slaves: modbusSlaves }),
      });

      if (!res || !res.ok) {
        throw new Error(await readError(res, 'Configurazione Modbus non inviata'));
      }

      const result = await res.json();
      const refused = (result.results || []).filter((r) => !r.ok);
      setActionMessage(
        `Configurazione Modbus richiesta per ${result.requested} device.` +
          (refused.length
            ? ` Non inviata a ${refused.length}: ${refused
                .map((r) => `${r.device_id} (${r.reason})`)
                .join(', ')}`
            : '')
      );
      await loadFleet(true);
    } catch (err) {
      setActionError(err?.message || 'Errore configurazione Modbus');
    } finally {
      setBusy(false);
    }
  };

  const requestServerUrlChange = async () => {
    const url = migrationUrl.trim();

    if (!url || selected.size === 0) return;

    const confirmed = window.confirm(
      `Spostare ${selected.size} device su ${url}?\n\n` +
        "Da quel momento interrogheranno l'altro server: gli aggiornamenti andranno " +
        'lanciati da lì, non più da questa pagina.'
    );
    if (!confirmed) return;

    try {
      setBusy(true);
      setActionMessage('');
      setActionError('');

      const res = await authFetch('/api/iot/fleet/server-url', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_ids: [...selected], server_url: url }),
      });

      if (!res || !res.ok) {
        throw new Error(await readError(res, 'Richiesta di spostamento non riuscita'));
      }

      const result = await res.json();
      setActionMessage(
        `Spostamento richiesto per ${result.requested} device. Lo applicano al prossimo contatto.`
      );
      setSelected(new Set());
      await loadFleet(true);
    } catch (err) {
      setActionError(err?.message || 'Errore richiesta di spostamento');
    } finally {
      setBusy(false);
    }
  };

  /*
    Download del .bin dalla dashboard: la rotta vuole il Bearer, quindi non
    basta un link. Si scarica con authFetch e si apre come blob.
  */
  const downloadBinary = async (fw) => {
    try {
      setActionError('');
      const res = await authFetch(`/api/iot/firmware/${fw._id}/file`);

      if (!res || !res.ok) {
        throw new Error(await readError(res, 'Download del firmware non riuscito'));
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `clotho_mb_fw_${fw.version}_${fw.variant || 'prod'}.bin`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch (err) {
      setActionError(err?.message || 'Errore download firmware');
    }
  };

  const cancelUpdate = async (deviceIds) => {
    if (deviceIds.length === 0) return;

    try {
      setBusy(true);
      setActionMessage('');
      setActionError('');

      const res = await authFetch('/api/iot/fleet/cancel', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_ids: deviceIds }),
      });

      if (!res || !res.ok) {
        throw new Error(await readError(res, 'Annullamento non riuscito'));
      }

      const result = await res.json();
      setActionMessage(`Annullati ${result.cancelled} aggiornamenti in attesa`);
      await loadFleet(true);
    } catch (err) {
      setActionError(err?.message || 'Errore annullamento');
    } finally {
      setBusy(false);
    }
  };

  const saveInstalledAt = async (deviceId, value) => {
    try {
      const res = await authFetch(
        `/api/iot/devices/${encodeURIComponent(deviceId)}/installed-at`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ installed_at: value || null }),
        }
      );

      if (!res || !res.ok) {
        throw new Error(await readError(res, 'Salvataggio data non riuscito'));
      }

      await loadFleet(true);
    } catch (err) {
      setActionError(err?.message || 'Errore salvataggio data installazione');
    }
  };

  const uploadFirmware = async (event) => {
    event.preventDefault();
    if (!uploadFile || !uploadVersion.trim()) return;

    try {
      setUploading(true);
      setActionMessage('');
      setActionError('');

      const form = new FormData();
      form.append('file', uploadFile);
      form.append('version', uploadVersion.trim());
      form.append('variant', uploadVariant);
      form.append('hw_revision', uploadHwRevision.trim());
      form.append('status', uploadStatus);
      form.append('notes', uploadNotes);
      form.append('is_latest', uploadLatest ? 'true' : 'false');

      // multipart: il Content-Type con il boundary lo mette il browser
      const res = await authFetch('/api/iot/firmware', {
        method: 'POST',
        body: form,
      });

      if (!res || !res.ok) {
        throw new Error(await readError(res, 'Caricamento firmware non riuscito'));
      }

      setActionMessage(`Firmware ${uploadVersion.trim()} (${uploadVariant}) caricato in archivio`);
      setUploadFile(null);
      setUploadVersion('');
      setUploadNotes('');
      setUploadInputKey((k) => k + 1);
      await loadFleet(true);
    } catch (err) {
      setActionError(err?.message || 'Errore caricamento firmware');
    } finally {
      setUploading(false);
    }
  };

  const patchFirmware = async (id, patch) => {
    try {
      setActionError('');
      const res = await authFetch(`/api/iot/firmware/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch),
      });

      if (!res || !res.ok) {
        throw new Error(await readError(res, 'Modifica firmware non riuscita'));
      }

      await loadFleet(true);
    } catch (err) {
      setActionError(err?.message || 'Errore modifica firmware');
    }
  };

  const deleteFirmware = async (firmware) => {
    if (
      !window.confirm(
        `Eliminare il firmware ${firmware.version} (${firmware.variant}) dall'archivio?`
      )
    )
      return;

    try {
      setActionError('');
      const res = await authFetch(`/api/iot/firmware/${firmware._id}`, { method: 'DELETE' });

      if (!res || !res.ok) {
        throw new Error(await readError(res, 'Eliminazione non riuscita'));
      }

      await loadFleet(true);
    } catch (err) {
      setActionError(err?.message || 'Errore eliminazione firmware');
    }
  };

  const summary = data?.summary;
  const notUpToDate = summary
    ? summary.outdated + summary.obsolete + summary.unknown + summary.failed
    : 0;
  const selectableFirmwares = firmwares.filter((fw) => fw.status !== 'deprecated');

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Aggiornamento firmware</h1>
            <p className="mt-1 text-sm text-gray-500">
              Parco macchine con la versione firmware riportata da ogni device, confrontata con
              la versione di riferimento{' '}
              {data?.latest_version ? (
                <span className="rounded-md bg-emerald-100 px-2 py-0.5 font-mono text-xs text-emerald-700">
                  {data.latest_version}
                </span>
              ) : (
                <span className="text-gray-400">(nessuna impostata: caricane una in archivio)</span>
              )}
              . Seleziona i device e metti in coda l&apos;aggiornamento: lo scaricano al
              prossimo contatto e confermano dopo il riavvio.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setShowArchive((v) => !v)}
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-700"
            >
              {showArchive ? 'Nascondi archivio' : 'Archivio firmware'}
            </button>
            <button
              type="button"
              onClick={() => loadFleet()}
              className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-cyan-700"
            >
              Aggiorna dati
            </button>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="rounded-lg bg-slate-700 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
            >
              Torna alla dashboard
            </button>
          </div>
        </div>

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Riepilogo */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <SummaryCard
            label="Device installati"
            value={summary ? summary.total : null}
            hint={summary ? `${summary.online} online negli ultimi 10 min` : ''}
            loading={loading}
          />
          <SummaryCard
            label="Aggiornati"
            value={summary ? summary.up_to_date : null}
            hint="Alla versione di riferimento"
            loading={loading}
            tone="emerald"
            onClick={() => updateFilter('status', 'up_to_date')}
          />
          <SummaryCard
            label="Da aggiornare"
            value={summary ? notUpToDate : null}
            hint={
              summary
                ? `${summary.outdated} non aggiornati · ${summary.obsolete} obsoleti · ${summary.unknown} sconosciuti · ${summary.failed} falliti`
                : ''
            }
            loading={loading}
            tone={notUpToDate > 0 ? 'amber' : 'default'}
            onClick={() => updateFilter('status', 'not_up_to_date')}
          />
          <SummaryCard
            label="In corso"
            value={summary ? summary.pending : null}
            hint={summary ? `${summary.diag_error} device con errori diagnostici` : ''}
            loading={loading}
            tone={summary && summary.pending > 0 ? 'cyan' : 'default'}
            onClick={() => updateFilter('status', 'pending')}
          />
        </div>

        {/* Distribuzione versioni */}
        {versionDistribution.length > 0 && (
          <Card className="shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-800">Versioni installate</h2>
            <div className="mt-4 space-y-2">
              {versionDistribution.map(([version, count]) => {
                const pct = devices.length ? Math.round((count / devices.length) * 100) : 0;
                const isLatest = version === data?.latest_version;
                return (
                  <button
                    key={version}
                    type="button"
                    onClick={() =>
                      updateFilter('version', version === '(nessuna)' ? '' : version)
                    }
                    className="flex w-full items-center gap-4 rounded-lg px-2 py-1 text-left transition hover:bg-gray-50"
                  >
                    <span
                      className={`w-40 shrink-0 font-mono text-sm ${isLatest ? 'text-emerald-700' : 'text-gray-700'}`}
                    >
                      {version}
                      {isLatest && (
                        <span className="ml-2 text-[10px] uppercase tracking-widest text-emerald-600">
                          rif.
                        </span>
                      )}
                    </span>
                    <span className="h-2 flex-1 overflow-hidden rounded-full bg-gray-200">
                      <span
                        className={`block h-full rounded-full ${isLatest ? 'bg-emerald-500' : 'bg-amber-400'}`}
                        style={{ width: `${Math.max(pct, 2)}%` }}
                      />
                    </span>
                    <span className="w-24 shrink-0 text-right text-sm text-gray-500">
                      {count} · {pct}%
                    </span>
                  </button>
                );
              })}
            </div>
          </Card>
        )}

        {/* Archivio firmware */}
        {showArchive && (
          <Card className="shadow-md p-6">
            <h2 className="text-lg font-semibold text-gray-800">Archivio firmware</h2>
            <p className="mt-1 text-sm text-gray-500">
              Carica il <span className="font-mono">.bin</span> dell&apos;app (non il merged)
              prodotto da <span className="font-mono">build.ps1</span>. La versione deve
              coincidere con <span className="font-mono">FW_VERSION</span> del firmware,
              altrimenti il device non risulterà mai aggiornato.
            </p>

            {canManage && (
              <form onSubmit={uploadFirmware} className="mt-5 grid gap-3 md:grid-cols-6">
                <label className="text-sm text-gray-700 md:col-span-2">
                  File .bin
                  <input
                    key={uploadInputKey}
                    type="file"
                    accept=".bin"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                    className={`${inputClass} file:mr-3 file:rounded-md file:border-0 file:bg-indigo-100 file:px-3 file:py-1 file:text-indigo-700`}
                  />
                </label>
                <label className="text-sm text-gray-700">
                  Versione
                  <input
                    type="text"
                    value={uploadVersion}
                    onChange={(e) => setUploadVersion(e.target.value)}
                    placeholder="1.0.1-mb"
                    className={`${inputClass} font-mono`}
                  />
                </label>
                <label className="text-sm text-gray-700">
                  Variante
                  <select
                    value={uploadVariant}
                    onChange={(e) => setUploadVariant(e.target.value)}
                    className={inputClass}
                  >
                    <option value="prod">prod</option>
                    <option value="debug">debug</option>
                  </select>
                </label>
                <label className="text-sm text-gray-700">
                  Revisione scheda
                  <input
                    type="text"
                    value={uploadHwRevision}
                    onChange={(e) => setUploadHwRevision(e.target.value)}
                    placeholder={DEFAULT_HW_REVISION}
                    title="Il device rifiuta il binario se la sua revisione è diversa. Vuoto = nessun controllo."
                    className={`${inputClass} font-mono`}
                  />
                </label>
                <label className="text-sm text-gray-700">
                  Stato
                  <select
                    value={uploadStatus}
                    onChange={(e) => setUploadStatus(e.target.value)}
                    className={inputClass}
                  >
                    <option value="stable">Stabile</option>
                    <option value="testing">In test</option>
                  </select>
                </label>
                <label className="flex items-end gap-2 pb-2 text-sm text-gray-700">
                  <Checkbox
                    checked={uploadLatest}
                    onChange={(e) => setUploadLatest(e.target.checked)}
                  />
                  Riferimento
                </label>
                <label className="text-sm text-gray-700 md:col-span-5">
                  Note di rilascio
                  <input
                    type="text"
                    value={uploadNotes}
                    onChange={(e) => setUploadNotes(e.target.value)}
                    placeholder="Cosa cambia in questa versione"
                    className={inputClass}
                  />
                </label>
                <div className="flex items-end">
                  <button
                    type="submit"
                    disabled={uploading || !uploadFile || !uploadVersion.trim()}
                    className="w-full rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {uploading ? 'Caricamento…' : 'Carica'}
                  </button>
                </div>
              </form>
            )}

            <div className="mt-5 overflow-x-auto">
              <table className="w-full min-w-[720px] text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wider text-gray-500">
                    <th className="py-2 pr-4">Versione</th>
                    <th className="py-2 pr-4">Variante</th>
                    <th className="py-2 pr-4">Stato</th>
                    <th className="py-2 pr-4">Dimensione</th>
                    <th className="py-2 pr-4">Caricato</th>
                    <th className="py-2 pr-4">Note</th>
                    <th className="py-2 pr-4">Device</th>
                    {canManage && <th className="py-2">Azioni</th>}
                  </tr>
                </thead>
                <tbody>
                  {firmwares.length === 0 && (
                    <tr>
                      <td colSpan={8} className="py-4 text-gray-500">
                        Nessun firmware in archivio.
                      </td>
                    </tr>
                  )}
                  {firmwares.map((fw) => {
                    const installed = devices.filter((d) => d.fw_version === fw.version).length;
                    return (
                      <tr key={fw._id} className="border-b border-gray-100 align-top">
                        <td className="py-3 pr-4 font-mono text-gray-800">
                          {fw.version}
                          {fw.is_latest && (
                            <Badge className="ml-2 bg-emerald-100 text-emerald-700">
                              riferimento
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 pr-4 text-gray-600">{fw.variant}</td>
                        <td className="py-3 pr-4 text-gray-600">{FW_STATUS_LABEL[fw.status]}</td>
                        <td className="py-3 pr-4 text-gray-600">{formatBytes(fw.size)}</td>
                        <td className="py-3 pr-4 text-gray-600">
                          {formatDate(fw.createdAt)}
                          {fw.uploaded_by_name && (
                            <span className="block text-xs text-gray-400">
                              {fw.uploaded_by_name}
                            </span>
                          )}
                        </td>
                        <td className="py-3 pr-4 text-gray-600">
                          {fw.notes || '—'}
                          <span className="block font-mono text-[10px] text-gray-400">
                            sha256 {String(fw.sha256 || '').slice(0, 16)}…
                            {fw.hw_revision ? ` · ${fw.hw_revision}` : ''}
                          </span>
                        </td>
                        <td className="py-3 pr-4 text-gray-600">
                          {installed}
                          {canDownloadBinary && (
                            <button
                              type="button"
                              onClick={() => downloadBinary(fw)}
                              className="ml-3 text-xs text-cyan-700 underline underline-offset-2 hover:text-cyan-900"
                            >
                              scarica
                            </button>
                          )}
                        </td>
                        {canManage && (
                          <td className="py-3">
                            <div className="flex flex-wrap gap-2">
                              {!fw.is_latest && fw.status !== 'deprecated' && (
                                <button
                                  type="button"
                                  onClick={() => patchFirmware(fw._id, { is_latest: true })}
                                  className={`${smallButtonClass} border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100`}
                                >
                                  Imposta riferimento
                                </button>
                              )}
                              {fw.status !== 'deprecated' ? (
                                <button
                                  type="button"
                                  onClick={() => patchFirmware(fw._id, { status: 'deprecated' })}
                                  className={`${smallButtonClass} border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100`}
                                >
                                  Depreca
                                </button>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => patchFirmware(fw._id, { status: 'stable' })}
                                  className={`${smallButtonClass} border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}
                                >
                                  Riattiva
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => deleteFirmware(fw)}
                                className={`${smallButtonClass} border-red-200 bg-red-50 text-red-700 hover:bg-red-100`}
                              >
                                Elimina
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Filtri */}
        <Card className="shadow-md p-6">
          <div className="grid gap-3 md:grid-cols-3 lg:grid-cols-5">
            <label className="text-sm text-gray-700 lg:col-span-2">
              Cerca
              <input
                type="search"
                value={filters.q}
                onChange={(e) => updateFilter('q', e.target.value)}
                placeholder="device, macchina, matricola, cliente, luogo, IP…"
                className={inputClass}
              />
            </label>
            <label className="text-sm text-gray-700">
              Cliente
              <select
                value={filters.customer}
                onChange={(e) => updateFilter('customer', e.target.value)}
                className={inputClass}
              >
                <option value="">Tutti</option>
                {(data?.filters?.customers || []).map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm text-gray-700">
              Luogo
              <select
                value={filters.site}
                onChange={(e) => updateFilter('site', e.target.value)}
                className={inputClass}
              >
                <option value="">Tutti</option>
                {(data?.filters?.sites || []).map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm text-gray-700">
              Versione FW
              <select
                value={filters.version}
                onChange={(e) => updateFilter('version', e.target.value)}
                className={`${inputClass} font-mono`}
              >
                <option value="">Tutte</option>
                {(data?.filters?.versions || []).map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
            <label className="text-sm text-gray-700">
              Stato firmware
              <select
                value={filters.status}
                onChange={(e) => updateFilter('status', e.target.value)}
                className={inputClass}
              >
                <option value="">Tutti</option>
                <option value="not_up_to_date">Da aggiornare (tutti)</option>
                <option value="up_to_date">Aggiornato</option>
                <option value="outdated">Non aggiornato</option>
                <option value="obsolete">Obsoleto</option>
                <option value="unknown">Sconosciuto</option>
                <option value="pending">In attesa</option>
                <option value="failed">Fallito</option>
              </select>
            </label>
            <label className="text-sm text-gray-700">
              Installato dal
              <input
                type="date"
                value={filters.installedFrom}
                onChange={(e) => updateFilter('installedFrom', e.target.value)}
                className={inputClass}
              />
            </label>
            <label className="text-sm text-gray-700">
              Installato al
              <input
                type="date"
                value={filters.installedTo}
                onChange={(e) => updateFilter('installedTo', e.target.value)}
                className={inputClass}
              />
            </label>
            <label className="text-sm text-gray-700">
              FW aggiornato dal
              <input
                type="date"
                value={filters.updatedFrom}
                onChange={(e) => updateFilter('updatedFrom', e.target.value)}
                className={inputClass}
              />
            </label>
            <label className="text-sm text-gray-700">
              FW aggiornato al
              <input
                type="date"
                value={filters.updatedTo}
                onChange={(e) => updateFilter('updatedTo', e.target.value)}
                className={inputClass}
              />
            </label>
            <div className="flex flex-wrap items-end gap-4 text-sm text-gray-700">
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={filters.onlyOnline}
                  onChange={(e) => updateFilter('onlyOnline', e.target.checked)}
                />
                Solo online
              </label>
              <label className="flex items-center gap-2">
                <Checkbox
                  checked={filters.onlyIssues}
                  onChange={(e) => updateFilter('onlyIssues', e.target.checked)}
                />
                Con anomalie
              </label>
              <button
                type="button"
                onClick={() => setFilters(emptyFilters)}
                className={`${smallButtonClass} border-gray-300 bg-white text-gray-600 hover:bg-gray-50`}
              >
                Azzera filtri
              </button>
            </div>
          </div>
        </Card>

        {/* Azioni di massa */}
        {canManage && (
          <Card className="shadow-md p-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-700">
              <span>
                <span className="font-semibold text-gray-900">{selected.size}</span> device
                selezionati
              </span>
              <button
                type="button"
                onClick={selectNotUpToDate}
                className={`${smallButtonClass} border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100`}
              >
                Seleziona tutti i da aggiornare (filtrati)
              </button>
              {selected.size > 0 && (
                <button
                  type="button"
                  onClick={() => setSelected(new Set())}
                  className={`${smallButtonClass} border-gray-300 bg-white text-gray-600 hover:bg-gray-50`}
                >
                  Deseleziona
                </button>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <select
                value={targetFirmwareId}
                onChange={(e) => setTargetFirmwareId(e.target.value)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 font-mono text-sm text-gray-800"
              >
                <option value="">— firmware di destinazione —</option>
                {selectableFirmwares.map((fw) => (
                  <option key={fw._id} value={fw._id}>
                    {fw.version} ({fw.variant}){fw.is_latest ? ' · riferimento' : ''}
                  </option>
                ))}
              </select>
              <button
                type="button"
                disabled={busy || selected.size === 0 || !targetFirmwareId}
                onClick={requestUpdate}
                className="rounded-lg bg-cyan-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy ? 'Invio…' : `Aggiorna ${selected.size || ''} device`}
              </button>
              <button
                type="button"
                disabled={busy || selected.size === 0}
                onClick={() => cancelUpdate([...selected])}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Annulla in attesa
              </button>
              <button
                type="button"
                onClick={() => setShowModbus((v) => !v)}
                className={`${smallButtonClass} border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100`}
              >
                {showModbus ? 'Chiudi sonde Modbus' : 'Sonde Modbus'}
              </button>
              <button
                type="button"
                onClick={() => setShowMigration((v) => !v)}
                className={`${smallButtonClass} border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100`}
              >
                {showMigration ? 'Chiudi migrazione' : 'Migrazione server'}
              </button>
            </div>
          </Card>
        )}

        {canManage && showModbus && (
          <Card className="shadow-md p-5 border border-amber-200">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h2 className="text-base font-semibold text-gray-900">Sonde Modbus sul modulo C</h2>
                <p className="mt-1 max-w-3xl text-sm text-gray-600">
                  Lista degli slave RS485 che il device interroga, fino a {MODBUS_MAX_SLAVES}. Il
                  modello XY-MD04 legge temperatura e umidità (funzione 4, registro 1, due
                  registri); con &quot;generic&quot; ogni registro letto diventa un valore
                  moltiplicato per la scala. La lista sostituisce quella sul device e resta
                  salvata in NVS.
                </p>
              </div>
              <button
                type="button"
                disabled={selected.size === 0}
                onClick={loadModbusFromSelected}
                className={`${smallButtonClass} border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}
              >
                Carica dal primo device selezionato
              </button>
            </div>

            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wider text-gray-500">
                  <tr>
                    <th className="py-2 pr-3">Attivo</th>
                    <th className="py-2 pr-3">Etichetta</th>
                    <th className="py-2 pr-3">Indirizzo</th>
                    <th className="py-2 pr-3">Modello</th>
                    <th className="py-2 pr-3">Funzione</th>
                    <th className="py-2 pr-3">Registro</th>
                    <th className="py-2 pr-3">N. reg.</th>
                    <th className="py-2 pr-3">Scala</th>
                    <th className="py-2 pr-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {modbusSlaves.map((slave, index) => {
                    const generic = slave.model === 'generic';
                    const field =
                      'w-full rounded-lg border border-gray-300 bg-white px-2 py-1 font-mono text-sm text-gray-800 disabled:bg-gray-100 disabled:text-gray-400';
                    return (
                      <tr key={index} className="border-t border-gray-100">
                        <td className="py-2 pr-3">
                          <Checkbox
                            checked={slave.enabled}
                            onChange={(e) => updateModbusSlave(index, { enabled: e.target.checked })}
                          />
                        </td>
                        <td className="py-2 pr-3">
                          <input
                            value={slave.label}
                            maxLength={11}
                            onChange={(e) =>
                              updateModbusSlave(index, { label: e.target.value.replace(/\s/g, '') })
                            }
                            className={`${field} w-24`}
                          />
                        </td>
                        <td className="py-2 pr-3">
                          <input
                            type="number"
                            min={1}
                            max={247}
                            value={slave.id}
                            onChange={(e) => updateModbusSlave(index, { id: Number(e.target.value) })}
                            className={`${field} w-20`}
                          />
                        </td>
                        <td className="py-2 pr-3">
                          <select
                            value={slave.model}
                            onChange={(e) => {
                              const model = e.target.value === 'generic' ? 'generic' : 'XY-MD04';
                              updateModbusSlave(
                                index,
                                model === 'XY-MD04' ? { model, fc: 4, reg: 1, n: 2, scale: 1 } : { model }
                              );
                            }}
                            className={`${field} w-28`}
                          >
                            <option value="XY-MD04">XY-MD04</option>
                            <option value="generic">generic</option>
                          </select>
                        </td>
                        <td className="py-2 pr-3">
                          <select
                            value={slave.fc}
                            disabled={!generic}
                            onChange={(e) =>
                              updateModbusSlave(index, { fc: Number(e.target.value) === 3 ? 3 : 4 })
                            }
                            className={`${field} w-16`}
                          >
                            <option value={3}>3</option>
                            <option value={4}>4</option>
                          </select>
                        </td>
                        <td className="py-2 pr-3">
                          <input
                            type="number"
                            min={0}
                            max={65535}
                            disabled={!generic}
                            value={slave.reg}
                            onChange={(e) => updateModbusSlave(index, { reg: Number(e.target.value) })}
                            className={`${field} w-24`}
                          />
                        </td>
                        <td className="py-2 pr-3">
                          <input
                            type="number"
                            min={1}
                            max={8}
                            disabled={!generic}
                            value={slave.n}
                            onChange={(e) => updateModbusSlave(index, { n: Number(e.target.value) })}
                            className={`${field} w-16`}
                          />
                        </td>
                        <td className="py-2 pr-3">
                          <input
                            type="number"
                            step="any"
                            disabled={!generic}
                            value={slave.scale}
                            onChange={(e) => updateModbusSlave(index, { scale: Number(e.target.value) })}
                            className={`${field} w-20`}
                          />
                        </td>
                        <td className="py-2 pr-3">
                          <button
                            type="button"
                            disabled={modbusSlaves.length <= 1}
                            onClick={() =>
                              setModbusSlaves((list) => list.filter((_, i) => i !== index))
                            }
                            className="text-xs text-gray-400 hover:text-red-600 disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            togli
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button
                type="button"
                disabled={modbusSlaves.length >= MODBUS_MAX_SLAVES}
                onClick={() =>
                  setModbusSlaves((list) => [...list, defaultModbusSlave(list.length + 1)])
                }
                className={`${smallButtonClass} border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}
              >
                Aggiungi slave
              </button>
              <button
                type="button"
                disabled={busy || selected.size === 0 || modbusSlaves.length === 0}
                onClick={requestModbusConfig}
                className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Invia a {selected.size || ''} device
              </button>
              <span className="text-xs text-gray-500">
                TEMP-07/HUM-01 dal primo XY-MD04, TEMP-08/HUM-02 dal secondo. Richiede firmware
                1.1.0-mb o successivo.
              </span>
            </div>
          </Card>
        )}

        {canManage && showMigration && (
          <Card className="shadow-md p-5 border border-violet-200">
            <h2 className="text-base font-semibold text-gray-900">
              Sposta le sonde su un altro server
            </h2>
            <p className="mt-1 max-w-3xl text-sm text-gray-600">
              Il device salva il nuovo indirizzo e da quel momento interroga quello: gli
              aggiornamenti si lanciano dal sito di destinazione, non più da qui. Non serve
              riprogrammare le schede. Richiede firmware 1.1.0-mb o successivo.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <input
                type="url"
                value={migrationUrl}
                onChange={(e) => setMigrationUrl(e.target.value)}
                placeholder="https://clothoio.com"
                className="min-w-[280px] flex-1 rounded-lg border border-gray-300 bg-white px-3 py-2 font-mono text-sm text-gray-800"
              />
              <button
                type="button"
                disabled={busy || selected.size === 0 || !migrationUrl.trim()}
                onClick={requestServerUrlChange}
                className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-violet-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Sposta {selected.size || ''} device
              </button>
            </div>
          </Card>
        )}

        {actionMessage && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {actionMessage}
          </div>
        )}
        {actionError && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {actionError}
          </div>
        )}

        {/* Tabella device */}
        <Card className="shadow-md overflow-x-auto">
          <table className="w-full min-w-[1100px] text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wider text-gray-500">
                {canManage && (
                  <th className="px-4 py-3">
                    <Checkbox checked={allFilteredSelected} onChange={toggleSelectAll} />
                  </th>
                )}
                <th className="px-4 py-3">Device / macchina</th>
                <th className="px-4 py-3">Cliente · luogo</th>
                <th className="px-4 py-3">Installato</th>
                <th className="px-4 py-3">Firmware</th>
                <th className="px-4 py-3">Stato</th>
                <th className="px-4 py-3">FW aggiornato</th>
                <th className="px-4 py-3">Ultimo contatto</th>
                <th className="px-4 py-3">Diagnostica</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={10} className="px-4 py-6 text-gray-500">
                    Caricamento…
                  </td>
                </tr>
              )}
              {!loading && filteredDevices.length === 0 && (
                <tr>
                  <td colSpan={10} className="px-4 py-6 text-gray-500">
                    Nessun device corrisponde ai filtri.
                  </td>
                </tr>
              )}
              {filteredDevices.map((device) => {
                const meta = STATUS_META[device.fw_status] || STATUS_META.unknown;
                const isExpanded = expanded === device.device_id;
                const diagLevel = device.last_diag?.level || '';
                const isSelected = selected.has(device.device_id);

                return (
                  <React.Fragment key={device.device_id}>
                    <tr
                      className={`border-b border-gray-100 align-top ${isSelected ? 'bg-cyan-50' : ''}`}
                    >
                      {canManage && (
                        <td className="px-4 py-3">
                          <Checkbox
                            checked={isSelected}
                            onChange={() => toggleSelected(device.device_id)}
                          />
                        </td>
                      )}
                      <td className="px-4 py-3">
                        <div className="font-mono text-xs text-gray-500">{device.device_id}</div>
                        <div className="text-gray-800">
                          {device.machine_name || device.machine_id || '—'}
                        </div>
                        {(device.fridge_serial || device.machine_serial) && (
                          <div className="text-xs text-gray-400">
                            {[device.fridge_serial, device.machine_serial]
                              .filter(Boolean)
                              .join(' · ')}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-gray-800">
                          {device.customer_name || (
                            <span className="text-gray-400">non assegnato</span>
                          )}
                        </div>
                        <div className="text-xs text-gray-500">
                          {[device.installation_site, device.room].filter(Boolean).join(' · ') ||
                            device.site_id ||
                            '—'}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {canManage ? (
                          <input
                            type="date"
                            defaultValue={toDateInput(device.installed_at)}
                            onBlur={(e) => {
                              if (e.target.value !== toDateInput(device.installed_at)) {
                                saveInstalledAt(device.device_id, e.target.value);
                              }
                            }}
                            className="rounded-lg border border-gray-300 bg-white px-2 py-1 text-xs text-gray-800"
                            title={`Fonte: ${device.installed_at_source || '—'}`}
                          />
                        ) : (
                          <span className="text-gray-800">
                            {formatDate(device.installed_at, false)}
                          </span>
                        )}
                        <div className="mt-1 text-[10px] uppercase tracking-widest text-gray-400">
                          {device.installed_at_source}
                        </div>
                      </td>
                      <td className="px-4 py-3 font-mono text-gray-800">
                        {device.fw_version || <span className="text-gray-400">—</span>}
                        {device.hw_revision && (
                          <div className="text-[10px] text-gray-400">{device.hw_revision}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <Badge title={meta.hint} className={meta.className}>
                          {meta.label}
                        </Badge>
                        {device.fw_status === 'pending' && device.fw_update?.target_version && (
                          <div className="mt-1 text-xs text-cyan-700">
                            → {device.fw_update.target_version} ·{' '}
                            {device.fw_update.phase && device.fw_update.phase !== 'queued'
                              ? `${OTA_PHASE_LABEL[device.fw_update.phase] || device.fw_update.phase}${
                                  device.fw_update.phase === 'downloading'
                                    ? ` ${device.fw_update.progress ?? 0}%`
                                    : ''
                                }`
                              : relativeTime(device.fw_update.requested_at)}
                          </div>
                        )}
                        {device.modbus_config?.pending && (
                          <div className="mt-1 text-xs text-amber-700">
                            config. Modbus in attesa
                          </div>
                        )}
                        {device.server_url_pending && (
                          <div className="mt-1 max-w-[220px] truncate text-xs text-violet-700">
                            spostamento su {device.server_url_pending}
                          </div>
                        )}
                        {device.fw_status === 'failed' && device.fw_update?.error && (
                          <div className="mt-1 max-w-[220px] text-xs text-red-600">
                            {device.fw_update.error}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-800">
                        {formatDate(device.fw_updated_at)}
                        {device.fw_previous_version && (
                          <div className="text-xs text-gray-400">
                            da {device.fw_previous_version}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span
                            className={`h-2 w-2 rounded-full ${device.online ? 'bg-emerald-500' : 'bg-gray-300'}`}
                          />
                          <span className="text-gray-800">{relativeTime(device.last_seen_at)}</span>
                        </div>
                        <div className="text-xs text-gray-400">{formatDate(device.last_seen_at)}</div>
                      </td>
                      <td className="px-4 py-3">
                        {diagLevel ? (
                          <Badge
                            className={
                              diagLevel === 'ok'
                                ? 'bg-emerald-100 text-emerald-700'
                                : diagLevel === 'warning'
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-red-100 text-red-700'
                            }
                          >
                            {diagLevel === 'ok'
                              ? 'OK'
                              : `${device.last_diag?.issues?.length || 0} anomalie`}
                          </Badge>
                        ) : (
                          <span className="text-xs text-gray-400">nessuna</span>
                        )}
                        <div className="text-xs text-gray-400">{relativeTime(device.last_diag_at)}</div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <button
                          type="button"
                          onClick={() => setExpanded(isExpanded ? null : device.device_id)}
                          className={`${smallButtonClass} border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}
                        >
                          {isExpanded ? 'Chiudi' : 'Dettagli'}
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="border-b border-gray-100 bg-gray-50">
                        <td colSpan={10} className="px-6 py-5">
                          <DeviceDetails
                            device={device}
                            canManage={canManage}
                            onCancel={() => cancelUpdate([device.device_id])}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </Card>

        <p className="text-xs text-gray-400">
          {filteredDevices.length} di {devices.length} device mostrati · aggiornamento automatico
          ogni 30 s
        </p>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, hint, loading, tone = 'default', onClick }) {
  const toneClass =
    tone === 'emerald'
      ? 'border-emerald-200 bg-emerald-50'
      : tone === 'amber'
        ? 'border-amber-200 bg-amber-50'
        : tone === 'cyan'
          ? 'border-cyan-200 bg-cyan-50'
          : 'border-gray-200 bg-white';

  const content = (
    <>
      <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">{label}</p>
      <p className="mt-2 text-3xl font-bold text-gray-800">
        {loading || value === null ? '…' : value}
      </p>
      <p className="mt-1 text-xs text-gray-500">{hint}</p>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`rounded-2xl border p-5 text-left shadow-sm transition hover:shadow-md ${toneClass}`}
      >
        {content}
      </button>
    );
  }

  return <div className={`rounded-2xl border p-5 shadow-sm ${toneClass}`}>{content}</div>;
}

function DeviceDetails({ device, canManage, onCancel }) {
  const diag = device.last_diag;
  const history = [...(device.fw_history || [])].reverse().slice(0, 10);
  const hasDiag = Boolean(diag && diag.level);

  return (
    <div className="grid gap-6 lg:grid-cols-3">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
          Aggiornamento
        </p>
        <dl className="mt-2 space-y-1 text-sm">
          <Row k="Stato" v={device.fw_update?.status || 'idle'} />
          <Row k="Versione richiesta" v={device.fw_update?.target_version || '—'} mono />
          <Row
            k="Richiesto"
            v={`${formatDate(device.fw_update?.requested_at)}${device.fw_update?.requested_by_name ? ` da ${device.fw_update.requested_by_name}` : ''}`}
          />
          <Row k="Completato" v={formatDate(device.fw_update?.completed_at)} />
          <Row k="Tentativi" v={String(device.fw_update?.attempts ?? 0)} />
          <Row
            k="Fase"
            v={
              device.fw_update?.phase
                ? `${OTA_PHASE_LABEL[device.fw_update.phase] || device.fw_update.phase} (${device.fw_update.progress ?? 0}%) · ${relativeTime(device.fw_update.phase_at)}`
                : '—'
            }
          />
          {device.fw_update?.error && <Row k="Errore" v={device.fw_update.error} />}
        </dl>
        {canManage && ['pending', 'failed'].includes(device.fw_update?.status || '') && (
          <button
            type="button"
            onClick={onCancel}
            className={`${smallButtonClass} mt-3 border-gray-300 bg-white text-gray-700 hover:bg-gray-50`}
          >
            {device.fw_update?.status === 'pending' ? 'Annulla aggiornamento' : 'Azzera esito'}
          </button>
        )}
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
          Ultima diagnostica
        </p>
        {hasDiag ? (
          <dl className="mt-2 space-y-1 text-sm">
            <Row k="Ricevuta" v={formatDate(device.last_diag_at)} />
            <Row k="Uptime" v={uptimeLabel(diag.uptime_s)} />
            <Row k="Ultimo reset" v={diag.reset_reason || '—'} />
            <Row k="Heap libero" v={diag.free_heap ? `${Math.round(diag.free_heap / 1024)} kB` : '—'} />
            <Row
              k="microSD"
              v={
                diag.sd_ready === null || diag.sd_ready === undefined
                  ? '—'
                  : diag.sd_ready
                    ? 'OK'
                    : 'assente/guasta'
              }
            />
            <Row
              k="PT100 valide"
              v={
                diag.pt100_valid_count === null || diag.pt100_valid_count === undefined
                  ? '—'
                  : `${diag.pt100_valid_count}/6`
              }
            />
            <Row
              k="Modbus"
              v={
                diag.modbus_ok === null || diag.modbus_ok === undefined
                  ? '—'
                  : diag.modbus_ok
                    ? 'OK'
                    : 'errori'
              }
            />
            <Row
              k="LoRa"
              v={
                diag.lora_detected === null || diag.lora_detected === undefined
                  ? '—'
                  : diag.lora_detected
                    ? 'rilevata'
                    : 'non rilevata'
              }
            />
            <Row k="Partizione OTA" v={diag.ota_partition || '—'} mono />
            {diag.calibration_mode && <Row k="Modalità" v="CALIBRAZIONE attiva" />}
          </dl>
        ) : (
          <p className="mt-2 text-sm text-gray-500">
            Nessuna diagnostica ricevuta (firmware precedente a 1.0.0-mb).
          </p>
        )}
        {hasDiag && diag.issues && diag.issues.length > 0 && (
          <ul className="mt-3 space-y-1 text-sm text-amber-700">
            {diag.issues.map((issue) => (
              <li key={issue}>• {issue}</li>
            ))}
          </ul>
        )}

        <p className="mt-4 text-xs font-semibold uppercase tracking-wider text-gray-500">
          Sonde Modbus
        </p>
        {device.modbus_config?.status && device.modbus_config.status !== 'idle' && (
          <p className="mt-1 text-xs text-gray-500">
            Configurazione{' '}
            {MODBUS_CFG_LABEL[device.modbus_config.status] || device.modbus_config.status}
            {device.modbus_config.requested_by_name
              ? ` · da ${device.modbus_config.requested_by_name}`
              : ''}
            {' · '}
            {relativeTime(device.modbus_config.completed_at || device.modbus_config.requested_at)}
            {device.modbus_config.error ? ` · ${device.modbus_config.error}` : ''}
          </p>
        )}
        {hasDiag && Array.isArray(diag.modbus_slaves) && diag.modbus_slaves.length > 0 ? (
          <ul className="mt-2 space-y-1 text-sm">
            {diag.modbus_slaves.map((s, i) => (
              <li key={`${s.id}-${i}`} className="flex flex-wrap gap-x-2 text-gray-700">
                <span className="font-mono">{s.label || `MB${i + 1}`}</span>
                <span className="text-gray-400">
                  id {s.id} · {s.model || '?'}
                </span>
                {s.enabled === false ? (
                  <span className="text-gray-400">spento</span>
                ) : s.valid ? (
                  <span className="font-mono text-emerald-700">
                    {(s.values || [])
                      .map((v, k) =>
                        s.model === 'XY-MD04' ? `${v}${k === 0 ? ' °C' : ' %'}` : String(v)
                      )
                      .join(' · ')}
                  </span>
                ) : (
                  <span className={s.problem ? 'text-red-600' : 'text-amber-700'}>
                    {s.last_error || 'nessuna lettura'} ({s.err ?? 0} errori)
                  </span>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-1 text-sm text-gray-500">Nessuno slave riportato.</p>
        )}
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500">
          Storico versioni
        </p>
        {history.length === 0 ? (
          <p className="mt-2 text-sm text-gray-500">Nessun cambio di versione registrato.</p>
        ) : (
          <ul className="mt-2 space-y-1 text-sm">
            {history.map((entry, index) => (
              <li key={`${entry.at}-${index}`} className="flex flex-wrap gap-x-2 text-gray-700">
                <span className="text-gray-400">{formatDate(entry.at)}</span>
                <span className="font-mono">
                  {entry.from_version || '?'} → {entry.version}
                </span>
                <span className="text-gray-400">({entry.source})</span>
                {!entry.ok && <span className="text-red-600">{entry.error}</span>}
              </li>
            ))}
          </ul>
        )}
        <p className="mt-3 text-xs text-gray-400">
          IP {device.ip_address || '—'} · RSSI {device.wifi_rssi ?? '—'} dBm · pairing{' '}
          {device.pairing_status || '—'}
        </p>
        <p className="mt-1 text-xs text-gray-400">
          Server interrogato:{' '}
          <span className="font-mono">{device.server_url || 'non riportato'}</span>
          {device.server_url_pending ? (
            <span className="text-violet-700"> · spostamento su {device.server_url_pending} in attesa</span>
          ) : null}
        </p>
      </div>
    </div>
  );
}

function Row({ k, v, mono = false }) {
  return (
    <div className="flex gap-3">
      <dt className="w-36 shrink-0 text-gray-500">{k}</dt>
      <dd className={`text-gray-800 ${mono ? 'font-mono' : ''}`}>{v}</dd>
    </div>
  );
}
